import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Stack,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Alert,
  AlertTitle,
  InputAdornment,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Pagination,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  Grid
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  DeleteOutlined as DeleteIcon,
  MicOutlined as MicIcon,
  Search as SearchIcon,
  SmartToy as SmartToyIcon,
  InsertDriveFile as FileIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Folder as FolderIcon,
  DownloadOutlined as DownloadIcon,
  ShareOutlined as ShareIcon,
  StarBorder as StarBorderIcon,
  EditOutlined as EditOutlineIcon,
  VisibilityOutlined as VisibilityIcon,
  AutorenewOutlined as RegenerateIcon,
  ExpandMore as ExpandMoreIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon
} from '@mui/icons-material';
import {
  Microphone as MicrophoneIcon,
  Waveform as WaveformIcon,
  Folders as FoldersIcon,
  Folder as FolderIconPhosphor,
  Lightning,
  Article,
  Files,
  FileText,
  Image as ImageIconPhosphor
} from '@phosphor-icons/react';
import { PlusCircle } from 'lucide-react';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isValid as isValidDate } from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useToast } from '@/components/Toast';
import ReactMarkdown from 'react-markdown';
import { UnifiedTabs, UnifiedTabsContainer } from '@/components/UnifiedTabs';
import remarkGfm from 'remark-gfm';
import {
  getCollectionById,
  updateCollection,
  addSessionToCollection,
  removeSessionFromCollection,
  uploadCollectionDocument,
  processCollectionSessions,
  downloadCollectionDocument,
  createCollectionDocumentPreviewUrl,
  regenerateCompanyDocument,
  regenerateCollectionDocuments
} from '@/services/sessionCollectionService';
import {
  deleteTranscriptionSession,
  getAllTranscriptionSessions
} from '@/services/transcriptionSessionService';
import {
  SessionCollection,
  SessionMembership
} from '@/types/sessionCollectionTypes';
import {
  TranscriptionSession,
  TranscriptionSessionStatus,
  getSessionStatusLabel
} from '@/types/transcriptionSessionTypes';
import { createSessionAndNavigate } from '@/services/sessionNavigationService';
import { getAllCompanyDocuments } from '@/services/companyDocumentService';
import {
  DocumentResult,
  LinkedCollectionResult
} from '@/types/companyDocumentTypes';
import { CollectionMultiChatUI } from '@/components/CollectionMultiChatUI';
import sessionNavigationService from '@/services/sessionNavigationService';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentPreviewDialog } from '@/components/DocumentPreviewDialog/DocumentPreviewDialog';
import {
  SortIconDropdown,
  type SortDirection,
  type SortOption
} from '@/components/SortIconDropdown/SortIconDropdown';

// --- Shared Types and Helpers (Duplicated from FilesPage for now) ---

// File type enum
enum FileType {
  Audio = 'audio',
  Document = 'document',
  Image = 'image',
  Folder = 'folder',
  Transcript = 'transcript'
}

// File interface
interface FileItem {
  id: string;
  name: string;
  type: FileType;
  size?: number;
  modifiedAt: string;
  createdAt: string;
  duration?: number; // For audio files
  status?: TranscriptionSessionStatus;
  isStarred?: boolean;
  session?: TranscriptionSession;
  collectionId?: string;
  sessionTitle?: string;
  linkedCollections?: LinkedCollectionResult[];
  sessionId?: string;
  documentUrl?: string;
  fileFormat?: string;
  documentStatus?: number; // Added for Collection logic
  searchIndexStatus?: number;
}

type FolderRegenerateMode = 'all' | 'failed';

const COMPANY_DOCUMENT_STATUS_FAILED = 4;
const COMPANY_DOCUMENT_SEARCH_INDEX_STATUS_FAILED = 4;

const folderFileSortOptions: SortOption[] = [
  {
    key: 'documentName',
    label: 'File name',
    ascendingLabel: 'A-Z',
    descendingLabel: 'Z-A'
  }
];

const formatDateDisplay = (
  dateString?: string | null,
  dateFormat = 'MMM d, yyyy'
): string => {
  if (!dateString) return 'N/A';
  const date = parseISO(dateString);
  return isValidDate(date) ? format(date, dateFormat) : 'Invalid Date';
};

const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getSessionStage = (session: SessionMembership) => {
  if (session.uploadingDocumentCount > 0 || session.processingDocumentCount > 0) {
    return { label: 'Processing', color: '#FF9800' };
  }

  if (session.failedDocumentCount > 0) {
    return { label: 'Failed', color: '#EF4444' };
  }

  if (session.status === TranscriptionSessionStatus.PendingApproval) {
    return { label: 'Pending Approval', color: '#3B82F6' };
  }

  if (
    session.status === TranscriptionSessionStatus.Approved &&
    (session.completedDocumentCount > 0 || !!session.aiSummary)
  ) {
    return { label: 'Completed', color: '#22C55E' };
  }

  return {
    label: getSessionStatusLabel(session.status as TranscriptionSessionStatus),
    color:
      session.status === TranscriptionSessionStatus.Approved
        ? '#22C55E'
        : session.status === TranscriptionSessionStatus.PendingApproval
          ? '#3B82F6'
          : session.status === TranscriptionSessionStatus.Processing
            ? '#FF9800'
            : '#9E9E9E'
  };
};

const shouldPollSession = (session: SessionMembership) =>
  session.uploadingDocumentCount > 0 ||
  session.processingDocumentCount > 0 ||
  (session.status === TranscriptionSessionStatus.Processing &&
    session.failedDocumentCount === 0);

// Convert document to file item
const documentToFileItem = (doc: DocumentResult): FileItem => {
  let type = FileType.Document;
  const ext = doc.fileFormat?.toLowerCase().replace('.', '') || '';
  if (['mp3', 'wav', 'm4a', 'ogg', 'webm'].includes(ext)) type = FileType.Audio;
  else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext))
    type = FileType.Image;

  return {
    id: doc.id,
    name: doc.documentName,
    type,
    size: doc.fileSizeBytes,
    modifiedAt: doc.createdAt,
    createdAt: doc.createdAt,
    sessionTitle: doc.sessionTitle,
    linkedCollections: doc.linkedCollections,
    sessionId: doc.sessionId,
    documentUrl: doc.documentUrl,
    fileFormat: doc.fileFormat,
    documentStatus: doc.documentStatus,
    searchIndexStatus: doc.searchIndexStatus
  };
};

// Get file type icon (unfilled Phosphor icons)
const getFileTypeIcon = (type: FileType, size: number = 24) => {
  switch (type) {
    case FileType.Audio:
      return <WaveformIcon size={size} weight="regular" />;
    case FileType.Document:
      return <FileText size={size} weight="regular" />;
    case FileType.Image:
      return <ImageIconPhosphor size={size} weight="regular" />;
    case FileType.Folder:
      return <FoldersIcon size={size} weight="regular" />;
    case FileType.Transcript:
      return <FileText size={size} weight="regular" />;
    default:
      return <FileText size={size} weight="regular" />;
  }
};

// --- End Shared Types ---

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {value === index && <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>{children}</Box>}
    </div>
  );
};

export const CollectionDetailPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const { id: collectionId } = useParams<{ id: string }>();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();
  const toast = useToast();
  const documentFileInputRef = useRef<HTMLInputElement | null>(null);
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, { progress: number; fileName: string }>>({});

  // State
  const [collection, setCollection] = useState<SessionCollection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedReferenceNumber, setEditedReferenceNumber] = useState('');

  // Add Recording Dialog
  const [addRecordingDialogOpen, setAddRecordingDialogOpen] = useState(false);
  const [availableSessions, setAvailableSessions] = useState<
    TranscriptionSession[]
  >([]);
  const [sessionSearchTerm, setSessionSearchTerm] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionTotalPages, setSessionTotalPages] = useState(1);
  const [sessionsPerPage] = useState(20);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(
    new Set()
  );
  const [isAddingSessions, setIsAddingSessions] = useState(false);
  const [sessionMenuAnchor, setSessionMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [selectedSessionForAction, setSelectedSessionForAction] =
    useState<SessionMembership | null>(null);
  const [sessionActionDialog, setSessionActionDialog] = useState<
    'remove' | 'delete' | null
  >(null);
  const [isSessionActionLoading, setIsSessionActionLoading] = useState(false);

  // Add Document Dialog
  const [addDocumentDialogOpen, setAddDocumentDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState<string | null>(
    null
  );

  // Files List State (New)
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [fileSortBy, setFileSortBy] = useState('documentName');
  const [fileSortDirection, setFileSortDirection] =
    useState<SortDirection>('asc');
  const [fileMenuAnchor, setFileMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [selectedItem, setSelectedItem] = useState<FileItem | null>(null);
  const [previewItem, setPreviewItem] = useState<FileItem | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewContentType, setPreviewContentType] = useState<string>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Start Recording state
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<FileItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [regeneratingDocumentIds, setRegeneratingDocumentIds] = useState<Set<string>>(
    new Set()
  );
  const [isRegeneratingFolder, setIsRegeneratingFolder] = useState(false);
  const [folderRegenerateMenuAnchor, setFolderRegenerateMenuAnchor] =
    useState<HTMLElement | null>(null);

  const hasFailedFolderDocuments =
    files.some(
      (item) =>
        item.documentStatus === COMPANY_DOCUMENT_STATUS_FAILED ||
        item.searchIndexStatus === COMPANY_DOCUMENT_SEARCH_INDEX_STATUS_FAILED
    ) ||
    collection?.documents?.some(
      (doc) =>
        doc.documentStatus === COMPANY_DOCUMENT_STATUS_FAILED ||
        doc.searchIndexStatus === COMPANY_DOCUMENT_SEARCH_INDEX_STATUS_FAILED
    ) ||
    collection?.sessions?.some((session) => session.failedDocumentCount > 0) ||
    false;

  // Load collection
  const loadCollection = useCallback(async (showLoading = true) => {
    if (!collectionId || !appUser) return;

    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await getCollectionById(
        collectionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        true, // include sessions
        false, // include conversations
        true // include documents
      );

      if (!data) {
        setError('Collection not found');
        return;
      }

      setCollection(data);
      setEditedTitle(data.title);
      setEditedDescription(data.description || '');
      setEditedReferenceNumber(data.referenceNumber || '');
    } catch (error) {
      console.error('Error loading collection:', error);
      setError('Failed to load collection. Please try again.');
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, [collectionId, appUser, getAccessTokenSilently, getAccessTokenWithPopup]);

  // Load Files (Documents) for this Collection
  const loadFiles = useCallback(async (showLoading = true) => {
    if (!collectionId || !appUser || !selectedUserRole) return;

    if (showLoading) {
      setFilesLoading(true);
    }
    setFilesError(null);

    try {
      const documentsResponse = await getAllCompanyDocuments(
        {
          pageNumber: 1,
          pageSize: 100, // Adjust as needed
          searchQuery: '',
          companyId: selectedUserRole.companyId,
          collectionId: collectionId,
          sortBy: fileSortBy || undefined,
          sortAscending: fileSortDirection
            ? fileSortDirection === 'asc'
            : undefined
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      const fileItems = documentsResponse.documents.map(documentToFileItem);
      setFiles(fileItems);
    } catch (err) {
      console.error('Error fetching files:', err);
      setFilesError('Failed to load files');
    } finally {
      if (showLoading) {
        setFilesLoading(false);
      }
    }
  }, [
    collectionId,
    appUser,
    selectedUserRole,
    fileSortBy,
    fileSortDirection,
    getAccessTokenSilently,
    getAccessTokenWithPopup
  ]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Polling for updates
  useEffect(() => {
    if (
      !collection ||
      !(
        collection.documents?.some((doc) => doc.documentStatus === 2) ||
        collection.sessions?.some((session) => shouldPollSession(session))
      )
    ) {
      return;
    }
    const interval = setInterval(() => {
      loadCollection(false);
      loadFiles(false);
    }, 5000);
    return () => clearInterval(interval);
  }, [collection, loadCollection, loadFiles]);

  // Filter files
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFiles(files);
    } else {
      const lowerSearch = searchQuery.toLowerCase();
      setFilteredFiles(
        files.filter((item) => item.name.toLowerCase().includes(lowerSearch))
      );
    }
  }, [searchQuery, files]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // File upload handler for drag & drop
  const handleFilesUpload = useCallback(async (uploadFiles: File[]) => {
    if (!appUser?.id || !collectionId) {
      toast.showError('Unable to upload files');
      return;
    }
    
    setIsUploadingFiles(true);

    try {
      // We need to upload to a session that's part of this collection
      // For now, create a new session for these files
      const { createTranscriptionSession } = await import('@/services/transcriptionSessionService');
      const { uploadGenericDocument } = await import('@/services/transcriptionDocumentService');

      const sessionInput = {
        title: uploadFiles.length === 1 
          ? uploadFiles[0].name 
          : `${uploadFiles.length} files - ${new Date().toLocaleDateString()}`,
        sessionStartTime: new Date().toISOString(),
        collectionId: collectionId
      };

      const createdSession = await createTranscriptionSession(
        sessionInput,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      // Initialize upload progress
      const uploads: Record<string, { progress: number; fileName: string }> = {};
      uploadFiles.forEach((file, index) => {
        const fileId = `upload-${Date.now()}-${index}`;
        uploads[fileId] = { progress: 0, fileName: file.name };
      });
      setUploadProgress(uploads);

      // Upload all files
      const uploadedDocs: string[] = [];
      
      for (let i = 0; i < uploadFiles.length; i++) {
        const file = uploadFiles[i];
        const fileId = Object.keys(uploads)[i];
        
        try {
          const uploadResult = await uploadGenericDocument(
            createdSession.id,
            file,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            {
              skipAnalysis: true,
              onProgress: (percent) => {
                setUploadProgress(prev => ({
                  ...prev,
                  [fileId]: { ...prev[fileId], progress: percent }
                }));
              }
            }
          );

          if (uploadResult.success && uploadResult.document) {
            uploadedDocs.push(uploadResult.document.id);
          }
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          toast.showError(`Failed to upload ${file.name}`);
        }

        // Remove from progress UI
        setTimeout(() => {
          setUploadProgress(prev => {
            const updated = { ...prev };
            delete updated[fileId];
            return updated;
          });
        }, 500);
      }

      if (uploadedDocs.length > 0) {
        await processCollectionSessions(
          collectionId,
          [createdSession.id],
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }

      // Refresh the files list
      setIsUploadingFiles(false);
      await loadCollection(false);
      await loadFiles(false);

      const fileCount = uploadedDocs.length;
      const fileNoun = fileCount === 1 ? 'file' : 'files';
      
      if (fileCount > 0) {
        toast.showSuccess(`${fileCount} ${fileNoun} uploaded. Processing started.`, {
          duration: 10000,
          actions: [
            {
              label: 'View Session',
              onClick: () => navigate(`/transcript/${createdSession.id}`),
              variant: 'primary'
            },
            {
              label: 'Ask AI',
              onClick: () => navigate(`/transcript/${createdSession.id}`, { 
                state: { openChat: true } 
              }),
              variant: 'secondary'
            }
          ]
        });
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.showError('Failed to upload files');
      setIsUploadingFiles(false);
    }
  }, [appUser, collectionId, loadCollection, loadFiles, toast, getAccessTokenSilently, getAccessTokenWithPopup, navigate]);

  // Drag and drop handlers
  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer?.types.includes('Files')) {
        setIsDragging(true);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.relatedTarget || (e.relatedTarget as Node).nodeName === 'HTML') {
        setIsDragging(false);
      }
    };

    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer?.files || []);
      if (droppedFiles.length > 0) {
        await handleFilesUpload(droppedFiles);
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [handleFilesUpload]);

  const handleSaveEdit = async () => {
    if (!collectionId || !appUser) return;

    try {
      await updateCollection(
        collectionId,
        {
          title: editedTitle,
          description: editedDescription || null,
          referenceNumber: editedReferenceNumber || null
        },
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      await loadCollection(false);
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating collection:', error);
      setError('Failed to update collection');
    }
  };

  const handleStartRecording = useCallback(async () => {
    if (!collectionId || !appUser || !collection) return;

    setIsCreatingSession(true);
    try {
      await createSessionAndNavigate({
        collectionId,
        userId: appUser.id,
        templateName: collection.title,
        explicitTitle: `${collection.title} - ${new Date().toLocaleString()}`,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        navigate
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to start recording');
    } finally {
      setIsCreatingSession(false);
    }
  }, [
    collectionId,
    appUser,
    collection,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    navigate
  ]);

  // File Action Handlers
  const handleItemClick = (item: FileItem) => {
    if ('sessionId' in item && item.sessionId) {
      sessionNavigationService.navigateToSession(item.sessionId, navigate);
    } else if ('documentUrl' in item && item.documentUrl) {
      window.open(item.documentUrl, '_blank');
    }
  };

  const handleItemMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    item: FileItem
  ) => {
    event.stopPropagation();
    setFileMenuAnchor(event.currentTarget);
    setSelectedItem(item);
  };

  const handleFileMenuClose = () => {
    setFileMenuAnchor(null);
    setSelectedItem(null);
  };

  const handleFolderRegenerateMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setFolderRegenerateMenuAnchor(event.currentTarget);
    },
    []
  );

  const handleFolderRegenerateMenuClose = useCallback(() => {
    setFolderRegenerateMenuAnchor(null);
  }, []);

  const handleDownloadFile = async (item: FileItem) => {
    if (!appUser) return;

    try {
      await downloadCollectionDocument(
        item.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        item.name
      );
    } catch (error) {
      console.error('Error downloading file:', error);
      setError('Failed to download file');
    }
  };

  const clearPreviewUrl = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const handlePreviewFile = async (item: FileItem) => {
    if (!appUser) return;

    clearPreviewUrl();
    setPreviewItem(item);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewContentType(undefined);

    try {
      const preview = await createCollectionDocumentPreviewUrl(
        item.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setPreviewUrl(preview.url);
      setPreviewContentType(preview.contentType);
    } catch (error) {
      console.error('Error previewing file:', error);
      setPreviewError('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    clearPreviewUrl();
    setPreviewItem(null);
    setPreviewContentType(undefined);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  const handleRegenerateFile = async (item: FileItem) => {
    if (!appUser || !appUser.isSuperAdmin) return;

    setRegeneratingDocumentIds((prev) => new Set(prev).add(item.id));
    handleFileMenuClose();

    try {
      await regenerateCompanyDocument(
        item.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      toast.showSuccess('Document regeneration queued.');
      await loadCollection(false);
      await loadFiles(false);
    } catch (error) {
      console.error('Error regenerating document:', error);
      toast.showError('Failed to regenerate document');
    } finally {
      setRegeneratingDocumentIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleRegenerateFolderDocuments = useCallback(async (mode: FolderRegenerateMode) => {
    if (!appUser || !appUser.isSuperAdmin || !collectionId || isRegeneratingFolder) {
      return;
    }

    handleFolderRegenerateMenuClose();
    setIsRegeneratingFolder(true);
    try {
      await regenerateCollectionDocuments(
        collectionId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        { failedOnly: mode === 'failed' }
      );
      toast.showSuccess(
        mode === 'failed'
          ? 'Failed document regeneration queued.'
          : 'Folder regeneration queued.'
      );
      await loadCollection(false);
      await loadFiles(false);
    } catch (error) {
      console.error('Error regenerating folder documents:', error);
      toast.showError(
        error instanceof Error
          ? error.message
          : 'Failed to regenerate folder documents'
      );
    } finally {
      setIsRegeneratingFolder(false);
    }
  }, [
    appUser,
    collectionId,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    handleFolderRegenerateMenuClose,
    isRegeneratingFolder,
    loadCollection,
    loadFiles,
    toast
  ]);

  const handleDeleteClick = (item: FileItem) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
    handleFileMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!appUser || !collectionId || !itemToDelete) return;

    setIsDeleting(true);
    try {
      if (itemToDelete.sessionId) {
        await removeSessionFromCollection(
          collectionId,
          itemToDelete.sessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      } else {
        // Document deletion not fully exposed yet in easy service
        console.warn('Document deletion logic pending');
      }
      await loadFiles(false); // Refresh
      await loadCollection(false); // Refresh counts
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (err) {
      console.error('Error deleting item', err);
      setError('Failed to remove item from collection');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const handleSessionMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    session: SessionMembership
  ) => {
    event.stopPropagation();
    setSessionMenuAnchor(event.currentTarget);
    setSelectedSessionForAction(session);
  };

  const handleSessionMenuClose = () => {
    setSessionMenuAnchor(null);
  };

  const handleOpenSessionActionDialog = (action: 'remove' | 'delete') => {
    setSessionActionDialog(action);
    handleSessionMenuClose();
  };

  const handleSessionActionCancel = () => {
    if (isSessionActionLoading) return;
    setSessionActionDialog(null);
    setSelectedSessionForAction(null);
  };

  const handleSessionActionConfirm = async () => {
    if (
      !appUser ||
      !collectionId ||
      !selectedSessionForAction ||
      !sessionActionDialog
    ) {
      return;
    }

    setIsSessionActionLoading(true);
    try {
      if (sessionActionDialog === 'remove') {
        await removeSessionFromCollection(
          collectionId,
          selectedSessionForAction.transcriptionSessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        toast.showSuccess('Session removed from folder');
      } else {
        const success = await deleteTranscriptionSession(
          selectedSessionForAction.transcriptionSessionId,
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        if (!success) {
          throw new Error('Session could not be deleted');
        }

        toast.showSuccess('Session deleted');
      }

      await loadCollection(false);
      await loadFiles(false);
      setSessionActionDialog(null);
      setSelectedSessionForAction(null);
    } catch (error) {
      console.error('Error handling session action:', error);
      toast.showError(
        sessionActionDialog === 'remove'
          ? 'Failed to remove session from folder'
          : 'Failed to delete session'
      );
    } finally {
      setIsSessionActionLoading(false);
    }
  };

  const handleOpenAddDocumentDialog = () => {
    if (isUploadingDocument) return;
    setDocumentUploadError(null);
    setSelectedFile(null);
    setDocumentName('');
    if (documentFileInputRef.current) {
      documentFileInputRef.current.value = '';
    }
    setAddDocumentDialogOpen(true);
  };

  // Header Actions Effect - Moved AFTER handler definitions
  useEffect(() => {
    const breadcrumbHeader = collection ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          onClick={() => navigate('/collections')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            cursor: 'pointer',
            transition: 'color 0.15s ease',
            '&:hover': { 
              color: theme.palette.text.primary 
            }
          }}
        >
          <FolderIconPhosphor size={16} weight="regular" />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              color: 'inherit'
            }}
          >
            Folders
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: '14px',
            color: theme.palette.text.disabled,
            userSelect: 'none',
            mx: -0.25
          }}
        >
          /
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.text.primary,
            maxWidth: isMobile ? '180px' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {collection.title}
        </Typography>
      </Box>
    ) : (
      <Skeleton
        variant="rounded"
        width={120}
        height={20}
        sx={{
          borderRadius: '6px',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'rgba(255,255,255,0.05)' 
            : 'rgba(0,0,0,0.06)'
        }}
      />
    );

    setHeaderText(breadcrumbHeader);

    // Show Upload/Record buttons across the primary collection tabs
    const getContextualButton = () => {
      if (activeTab === 0 || activeTab === 1 || activeTab === 2) {
        const isRegenerateDisabled = isRegeneratingFolder || files.length === 0;
        const regenerateMenu = appUser?.isSuperAdmin ? (
          <Menu
            id="folder-regenerate-menu"
            anchorEl={folderRegenerateMenuAnchor}
            open={Boolean(folderRegenerateMenuAnchor)}
            onClose={handleFolderRegenerateMenuClose}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            <MenuItem
              onClick={() => handleRegenerateFolderDocuments('all')}
              disabled={isRegeneratingFolder || files.length === 0}
            >
              <ListItemIcon>
                <RegenerateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Regenerate all</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => handleRegenerateFolderDocuments('failed')}
              disabled={
                isRegeneratingFolder || files.length === 0 || !hasFailedFolderDocuments
              }
            >
              <ListItemIcon>
                <RegenerateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Regenerate failed</ListItemText>
            </MenuItem>
          </Menu>
        ) : null;
        
        // Mobile: Icon buttons only (Record on left, Upload on right)
        if (isMobile) {
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {appUser?.isSuperAdmin && (
                <>
                  <IconButton
                    aria-label="Open regenerate folder documents menu"
                    aria-controls={
                      folderRegenerateMenuAnchor ? 'folder-regenerate-menu' : undefined
                    }
                    aria-haspopup="menu"
                    onClick={handleFolderRegenerateMenuOpen}
                    disabled={isRegenerateDisabled}
                    sx={{
                      borderRadius: '6px',
                      width: 36,
                      height: 36
                    }}
                  >
                    {isRegeneratingFolder ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RegenerateIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                  {regenerateMenu}
                </>
              )}
              <IconButton
                onClick={handleStartRecording}
                disabled={isCreatingSession || !collection}
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#fff',
                  borderRadius: '6px',
                  width: 36,
                  height: 36,
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db'}`,
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#f9fafb',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#9ca3af'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f9fafb',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb',
                    color: theme.palette.text.disabled
                  }
                }}
              >
                <MicrophoneIcon size={18} weight="regular" />
              </IconButton>
              <IconButton
                onClick={() => {
                  navigate('/create/upload', {
                    state: {
                      preSelectedCollectionId: collectionId,
                      preSelectedCollectionName: collection?.title,
                      autoOpenFilePicker: true
                    }
                  });
                }}
                sx={{
                  backgroundColor: '#e6f226',
                  color: '#030766',
                  borderRadius: '6px',
                  width: 36,
                  height: 36,
                  '&:hover': {
                    backgroundColor: '#d4e022'
                  }
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          );
        }
        
        // Desktop: Full buttons with labels
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {appUser?.isSuperAdmin && (
              <>
                <Button
                  variant="outlined"
                  startIcon={
                    isRegeneratingFolder ? (
                      <CircularProgress size={14} />
                    ) : (
                      <RegenerateIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  onClick={handleFolderRegenerateMenuOpen}
                  disabled={isRegenerateDisabled}
                  aria-controls={
                    folderRegenerateMenuAnchor ? 'folder-regenerate-menu' : undefined
                  }
                  aria-haspopup="menu"
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5
                  }}
                >
                  {isRegeneratingFolder ? 'Regenerating...' : 'Regenerate'}
                </Button>
                {regenerateMenu}
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<MicrophoneIcon size={18} weight="regular" />}
              onClick={handleStartRecording}
              disabled={isCreatingSession || !collection}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#fff',
                borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db',
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f9fafb',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : '#9ca3af'
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f9fafb',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#e5e7eb',
                  color: theme.palette.text.disabled
                }
              }}
            >
              Record
            </Button>
            <Button
              variant="contained"
              startIcon={<PlusCircle size={18} />}
              onClick={() => {
                navigate('/create/upload', {
                  state: {
                    preSelectedCollectionId: collectionId,
                    preSelectedCollectionName: collection?.title,
                    autoOpenFilePicker: true
                  }
                });
              }}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                backgroundColor: '#e6f226',
                color: '#030766',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#d4e022',
                  boxShadow: 'none'
                }
              }}
            >
              Upload
            </Button>
          </Box>
        );
      }
      return null;
    };

    setMenuActions(getContextualButton());
    return () => {
      setHeaderText('');
      setMenuActions([]);
    };
  }, [
    collection,
    activeTab,
    setHeaderText,
    setMenuActions,
    handleStartRecording,
    isCreatingSession,
    theme.palette.mode,
    navigate,
    theme.palette.text.secondary,
    theme.palette.text.primary,
    isMobile,
    collectionId,
    collection?.title,
    appUser?.isSuperAdmin,
    files.length,
    folderRegenerateMenuAnchor,
    handleFolderRegenerateMenuClose,
    handleFolderRegenerateMenuOpen,
    hasFailedFolderDocuments,
    isRegeneratingFolder,
    handleRegenerateFolderDocuments
  ]);

  const loadAvailableSessions = useCallback(
    async (page: number, searchTerm?: string) => {
      if (!appUser) return;

      setLoadingSessions(true);

      try {
        const paginatedResponse = await getAllTranscriptionSessions(
          {
            userId: appUser.id,
            pageNumber: page,
            pageSize: sessionsPerPage,
            sortBy: 'sessionstarttime',
            sortAscending: false,
            searchQuery: searchTerm || undefined
          },
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );

        // Filter out sessions already in collection
        const existingSessionIds = new Set(
          collection?.sessions.map((s) => s.transcriptionSessionId) || []
        );
        const filteredSessions = paginatedResponse.sessions.filter(
          (s) => !existingSessionIds.has(s.id)
        );

        setAvailableSessions(filteredSessions);
        setSessionTotalPages(paginatedResponse.totalPages);
      } catch (error) {
        console.error('Error loading sessions:', error);
        setError('Failed to load available sessions');
      } finally {
        setLoadingSessions(false);
      }
    },
    [
      appUser,
      getAccessTokenSilently,
      getAccessTokenWithPopup,
      collection,
      sessionsPerPage
    ]
  );

  const handleOpenAddRecordingDialog = async () => {
    setSessionPage(1);
    setSessionSearchTerm('');
    setSelectedSessionIds(new Set());
    setAddRecordingDialogOpen(true);
    await loadAvailableSessions(1);
  };

  const handleSessionPageChange = (_event: unknown, newPage: number) => {
    setSessionPage(newPage);
    loadAvailableSessions(newPage, sessionSearchTerm);
  };

  const handleToggleSessionSelection = (sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleAddSelectedSessions = async () => {
    if (!collectionId || !appUser || selectedSessionIds.size === 0) return;

    setIsAddingSessions(true);

    try {
      // Add all selected sessions
      await Promise.all(
        Array.from(selectedSessionIds).map((sessionId) =>
          addSessionToCollection(
            collectionId,
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          )
        )
      );

      await loadCollection(false);
      await loadFiles(false);
      setAddRecordingDialogOpen(false);
      setSelectedSessionIds(new Set());
      setSessionSearchTerm('');
      await processCollectionSessions(
        collectionId,
        Array.from(selectedSessionIds),
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      await loadCollection(false);
      await loadFiles(false);
    } catch (error) {
      console.error('Error adding sessions:', error);
      setError('Failed to add recordings to collection');
    } finally {
      setIsAddingSessions(false);
    }
  };

  // Debounced search for sessions
  useEffect(() => {
    if (!addRecordingDialogOpen) return;

    const timeout = setTimeout(() => {
      setSessionPage(1);
      loadAvailableSessions(1, sessionSearchTerm);
    }, 500);

    return () => clearTimeout(timeout);
  }, [sessionSearchTerm, addRecordingDialogOpen, loadAvailableSessions]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setDocumentName(file.name);
      setDocumentUploadError(null);
      event.target.value = '';
    }
  };

  const handleUploadDocument = async () => {
    if (
      !collectionId ||
      !appUser ||
      !selectedFile ||
      !documentName.trim() ||
      isUploadingDocument
    ) {
      return;
    }

    setIsUploadingDocument(true);
    setDocumentUploadError(null);

    try {
      await uploadCollectionDocument(
        collectionId,
        selectedFile,
        documentName.trim(),
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );

      await loadCollection(false);
      await loadFiles(false);

      setAddDocumentDialogOpen(false);
      setSelectedFile(null);
      setDocumentName('');
      if (documentFileInputRef.current) {
        documentFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error uploading collection document:', error);
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to upload document. Please try again.';
      setDocumentUploadError(message);
    } finally {
      setIsUploadingDocument(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Skeleton 
            variant="circular" 
            width={36} 
            height={36}
            sx={{ 
              bgcolor: theme.palette.mode === 'dark' 
                ? 'rgba(255,255,255,0.05)' 
                : 'rgba(0,0,0,0.06)'
            }}
          />
          <Box sx={{ flex: 1 }}>
            <Skeleton 
              width="30%" 
              height={28}
              sx={{ 
                mb: 0.5,
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.06)'
              }}
            />
            <Skeleton 
              width="20%" 
              height={18}
              sx={{ 
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.06)'
              }}
            />
          </Box>
        </Box>
        
        {/* Tab skeleton */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton 
              key={i}
              variant="rounded" 
              width={80} 
              height={32}
              sx={{ 
                borderRadius: '6px',
                bgcolor: theme.palette.mode === 'dark' 
                  ? 'rgba(255,255,255,0.05)' 
                  : 'rgba(0,0,0,0.06)'
              }}
            />
          ))}
        </Box>
        
        {/* Content skeleton cards */}
        <Stack spacing={1}>
          {[1, 2, 3, 4].map((i) => (
            <Card
              key={i}
              sx={{
                background: theme.palette.background.paper,
                borderRadius: '6px',
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                p: 2,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5
              }}
            >
              <Skeleton 
                variant="rounded" 
                width={36} 
                height={36}
                sx={{ 
                  borderRadius: '6px',
                  bgcolor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.05)' 
                    : 'rgba(0,0,0,0.06)'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Skeleton 
                  width="45%" 
                  height={20}
                  sx={{ 
                    mb: 0.5,
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }}
                />
                <Skeleton 
                  width="30%" 
                  height={16}
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }}
                />
              </Box>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  if (error && !collection) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/collections')}
          sx={{ mt: 2 }}
        >
          Back to Folders
        </Button>
      </Box>
    );
  }

  if (!collection) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: isMobile ? 'calc(100vh - 128px)' : 'calc(100vh - 80px)', // Fixed to viewport minus header
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Drag overlay - Minimal with subtle grey border */}
      {isDragging && (
        <Box
          sx={{
            position: 'fixed',
            top: 8,
            left: 8,
            right: 8,
            bottom: 8,
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.3)' 
              : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            pointerEvents: 'none',
            // Subtle grey dotted border with rounded corners
            border: theme.palette.mode === 'dark' 
              ? '2px dotted rgba(255, 255, 255, 0.25)' 
              : '2px dotted rgba(0, 0, 0, 0.2)',
            borderRadius: '16px',
            boxSizing: 'border-box'
          }}
        />
      )}

      {/* Upload Progress Snackbar */}
      {isUploadingFiles && Object.keys(uploadProgress).length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 10000,
            minWidth: 320,
            maxWidth: 400,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: '8px',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0, 0, 0, 0.5)'
              : '0 8px 24px rgba(0, 0, 0, 0.15)',
            p: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              Uploading files
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary }}>
              {Object.keys(uploadProgress).length} {Object.keys(uploadProgress).length === 1 ? 'file' : 'files'}
            </Typography>
          </Box>
          <Stack spacing={1}>
            {Object.entries(uploadProgress).map(([fileId, { progress, fileName }]) => (
              <Box key={fileId}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      mr: 1
                    }}
                  >
                    {fileName}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: theme.palette.text.secondary, flexShrink: 0 }}>
                    {Math.round(progress)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 4,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                    borderRadius: 2,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${progress}%`,
                      height: '100%',
                      backgroundColor: theme.palette.primary.main,
                      transition: 'width 0.3s ease',
                      borderRadius: 2
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      {/* Main Content Grid */}
      <Grid 
        container 
        spacing={{ xs: 0, md: 1 }} 
        sx={{ 
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          px: { xs: 0, md: 2 },
          pt: 2,
          pb: 2
        }}
      >
        {/* Left Column - Main Content */}
        <Grid
          size={{ xs: 12, md: collection?.id && !isMobile ? 6.6 : 12 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            pl: 0
          }}
        >
          {/* Main tabbed content box */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              bgcolor: 'background.paper',
              borderRadius: '10px',
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            }}
          >
            {/* Tabs */}
            <UnifiedTabsContainer>
              <UnifiedTabs
                tabs={[
                  { key: 0, label: 'Summary', icon: <Article size={16} weight="regular" /> },
                  {
                    key: 1,
                    label: 'Sessions',
                    icon: <MicrophoneIcon size={16} weight="regular" />,
                    count: collection.sessions.length
                  },
                  { key: 2, label: 'Files', icon: <Files size={16} weight="regular" />, count: files.length }
                ]}
                activeTab={activeTab}
                onTabChange={(key) => setActiveTab(key as number)}
              />
            </UnifiedTabsContainer>

            {/* Error Alert */}
            {error && (
              <Alert
                severity="error"
                sx={{ mx: isMobile ? 2 : 4, mt: 2 }}
                onClose={() => setError(null)}
              >
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {/* Tab Panels */}
            <Box
              sx={{
                flex: 1,
                overflow: 'hidden',
                px: 0,
                pt: 0,
                pb: 0,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                background: theme.palette.background.paper
              }}
            >
              {/* Summary Tab */}
              <TabPanel value={activeTab} index={0}>
                <Box sx={{ overflow: 'auto', height: '100%', px: 3, pt: 2, pb: 3 }}>
            {/* AI Summary */}
            <Card
              sx={{
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                mb: 3
              }}
            >
              <CardContent sx={{ p: '24px' }}>
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    mb: 2.5,
                    textAlign: 'left',
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px'
                  }}
                >
                  AI Summary
                </Typography>

                {collection.aiSummary ? (
                  <Box>
                    <Box
                      sx={{
                        fontSize: '13px',
                        color: theme.palette.text.primary,
                        lineHeight: 1.6,
                        textAlign: 'left',
                        '& p': {
                          margin: '0 0 1.25em 0',
                          '&:last-child': { marginBottom: 0 }
                        },
                        '& h1, & h2, & h3': {
                          marginTop: '2em',
                          marginBottom: '0.75em',
                          fontWeight: 600,
                          lineHeight: 1.4,
                          color: theme.palette.text.primary,
                          '&:first-of-type': { marginTop: 0 }
                        },
                        '& h1': { fontSize: '15px' },
                        '& h2': { fontSize: '14px' },
                        '& h3': { fontSize: '13px' },
                        '& ul, & ol': {
                          margin: '0.75em 0',
                          paddingLeft: '1.5em'
                        },
                        '& li': { margin: '0.5em 0', lineHeight: 1.6 },
                        '& strong': {
                          fontWeight: 600,
                          color: theme.palette.text.primary
                        },
                        '& code': {
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.08)'
                              : 'rgba(0, 0, 0, 0.04)',
                          padding: '0.15em 0.4em',
                          borderRadius: '4px',
                          fontSize: '0.92em',
                          fontFamily: 'ui-monospace, monospace'
                        },
                        '& a': {
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' }
                        }
                      }}
                    >
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {collection.aiSummary}
                      </ReactMarkdown>
                    </Box>
                    {collection.aiSummaryGeneratedAt && (
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: theme.palette.text.secondary,
                          mt: 2.5,
                          textAlign: 'left',
                          fontStyle: 'italic'
                        }}
                      >
                        Generated{' '}
                        {formatDateDisplay(
                          collection.aiSummaryGeneratedAt,
                          'MMM d, yyyy • h:mm a'
                        )}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                    <Typography
                      variant="body1"
                      sx={{ color: theme.palette.text.secondary, mb: 1 }}
                    >
                      No summary available
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.disabled }}
                    >
                      Upload files or record to generate a summary
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
                </Box>
              </TabPanel>

              {/* Sessions Tab */}
              <TabPanel value={activeTab} index={1}>
                <Box sx={{ overflow: 'auto', height: '100%', px: 3, pt: 2, pb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                      mb: 2
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          textAlign: 'left'
                        }}
                      >
                        Session Processing
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.secondary,
                          textAlign: 'left'
                        }}
                      >
                        Sessions in this folder and the current processing state of their documents.
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddRecordingDialog}
                      sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, textTransform: 'none' }}
                    >
                      Add Existing Sessions
                    </Button>
                  </Box>

                  {collection.sessions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.05)'
                              : '#f3f4f6',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          color: '#9ca3af'
                        }}
                      >
                        <MicrophoneIcon size={24} weight="regular" />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          mb: 1
                        }}
                      >
                        No sessions in this folder yet
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: theme.palette.text.secondary,
                          lineHeight: 1.6
                        }}
                      >
                        Upload files to create a session here, record directly into the folder, or add an existing session.
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1.25}>
                      {collection.sessions.map((session) => {
                        const stage = getSessionStage(session);
                        return (
                          <Card
                            key={session.id}
                            onClick={() =>
                              sessionNavigationService.navigateToSession(
                                session.transcriptionSessionId,
                                navigate
                              )
                            }
                            sx={{
                              background: theme.palette.background.paper,
                              borderRadius: '8px',
                              boxShadow: 'none',
                              border: `1px solid ${theme.palette.divider}`,
                              cursor: 'pointer',
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                borderColor:
                                  theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                                backgroundColor:
                                  theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.02)'
                                    : '#fafafa'
                              }
                            }}
                          >
                            <Box sx={{ p: 2.25 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: 1.5,
                                  mb: 1.5
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    sx={{
                                      fontSize: '14px',
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                      textAlign: 'left',
                                      lineHeight: 1.4,
                                      mb: 0.5
                                    }}
                                  >
                                    {session.sessionTitle || 'Untitled Session'}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: '12px',
                                      color: theme.palette.text.secondary,
                                      textAlign: 'left'
                                    }}
                                  >
                                    {formatDateDisplay(
                                      session.sessionStartTime,
                                      'MMM d, yyyy • h:mm a'
                                    )}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    flexShrink: 0
                                  }}
                                >
                                  <Chip
                                    label={stage.label}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      color: stage.color,
                                      border: `1px solid ${stage.color}`,
                                      backgroundColor: `${stage.color}14`
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    aria-label={`Actions for ${session.sessionTitle || 'Untitled Session'}`}
                                    aria-controls={
                                      sessionMenuAnchor
                                        ? 'folder-session-actions-menu'
                                        : undefined
                                    }
                                    aria-haspopup="menu"
                                    onClick={(event) =>
                                      handleSessionMenuClick(event, session)
                                    }
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      color: theme.palette.text.secondary
                                    }}
                                  >
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                  mb: 1.5
                                }}
                              >
                                <Chip
                                  label={`${session.documentCount} document${session.documentCount === 1 ? '' : 's'}`}
                                  size="small"
                                  sx={{ height: 22, fontSize: '11px' }}
                                />
                                {session.processingDocumentCount > 0 && (
                                  <Chip
                                    label={`${session.processingDocumentCount} processing`}
                                    size="small"
                                    sx={{ height: 22, fontSize: '11px' }}
                                  />
                                )}
                                {session.failedDocumentCount > 0 && (
                                  <Chip
                                    label={`${session.failedDocumentCount} failed`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '11px',
                                      color: theme.palette.error.main,
                                      border: `1px solid ${theme.palette.error.main}`,
                                      backgroundColor: `${theme.palette.error.main}14`
                                    }}
                                  />
                                )}
                                {session.requiresApproval && (
                                  <Chip
                                    label="Requires approval"
                                    size="small"
                                    sx={{ height: 22, fontSize: '11px' }}
                                  />
                                )}
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  color: theme.palette.text.secondary,
                                  textAlign: 'left',
                                  lineHeight: 1.6,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden'
                                }}
                              >
                                {session.aiSummary || 'No session summary yet.'}
                              </Typography>

                              <Typography
                                sx={{
                                  mt: 1.5,
                                  fontSize: '11px',
                                  color: theme.palette.text.disabled,
                                  textAlign: 'left'
                                }}
                              >
                                Last updated{' '}
                                {formatDateDisplay(
                                  session.aiSummaryGeneratedAt || session.updatedAt || session.addedAt,
                                  'MMM d, yyyy • h:mm a'
                                )}
                              </Typography>
                            </Box>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </TabPanel>

              {/* Files Tab */}
              <TabPanel value={activeTab} index={2}>
                <Box sx={{ overflow: 'auto', height: '100%', px: 3, pt: 2, pb: 3 }}>
            {/* Search and View Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <TextField
                placeholder="Search files..."
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '13px',
                    backgroundColor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    height: 40,
                    '&:hover': {
                      borderColor:
                        theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
                    },
                    '&.Mui-focused': {
                      borderColor: '#6b7280',
                      boxShadow: 'none'
                    }
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        sx={{
                          fontSize: 18,
                          color: theme.palette.text.secondary
                        }}
                      />
                    </InputAdornment>
                  )
                }}
              />
              <SortIconDropdown
                options={folderFileSortOptions}
                currentSort={fileSortBy}
                currentDirection={fileSortDirection}
                onSortChange={(key, direction) => {
                  setFileSortBy(key);
                  setFileSortDirection(direction);
                }}
                size={isMobile ? 'medium' : 'small'}
              />
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
                sx={{
                  display: 'none', // Hide the toggle
                  '& .MuiToggleButton-root': {
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '6px',
                    px: 1.5,
                    py: 0.5,
                    '&.Mui-selected': {
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : '#f3f4f6',
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.15)'
                            : '#e5e7eb'
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="grid">
                  <GridViewIcon sx={{ fontSize: 18 }} />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon sx={{ fontSize: 18 }} />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Files List */}
            {filesLoading ? (
              <Stack spacing={1}>
                {[1, 2, 3].map((i) => (
                  <Card
                    key={i}
                    sx={{
                      background: theme.palette.background.paper,
                      borderRadius: '6px',
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      p: 2,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5
                    }}
                  >
                    <Skeleton 
                      variant="rounded" 
                      width={36} 
                      height={36}
                      sx={{ 
                        borderRadius: '6px',
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)' 
                          : 'rgba(0,0,0,0.06)'
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton 
                        width="50%" 
                        height={20}
                        sx={{ 
                          mb: 0.5,
                          bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.05)' 
                            : 'rgba(0,0,0,0.06)'
                        }}
                      />
                      <Skeleton 
                        width="35%" 
                        height={16}
                        sx={{ 
                          bgcolor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.05)' 
                            : 'rgba(0,0,0,0.06)'
                        }}
                      />
                    </Box>
                  </Card>
                ))}
              </Stack>
            ) : filteredFiles.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    background:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : '#f3f4f6',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: '#9ca3af'
                  }}
                >
                  <FileIcon sx={{ fontSize: 24 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  {searchQuery ? 'No files match your search' : 'Drag files here to upload'}
                </Typography>
                {!searchQuery && (
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: theme.palette.text.secondary,
                      mb: 2,
                      lineHeight: 1.6
                    }}
                  >
                    Drop audio, video, images, PDFs, or documents anywhere on this page
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    opacity: 0.8,
                    fontStyle: 'italic'
                  }}
                >
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Or use the Upload button above to browse files, or Record to capture audio'}
                </Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {filteredFiles.map((item) => (
                  <Card
                    key={item.id}
                    onClick={() => handleItemClick(item)}
                    sx={{
                      background: theme.palette.background.paper,
                      borderRadius: '6px',
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      transition: 'all 0.15s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor:
                          theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                        backgroundColor:
                          theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.02)'
                            : '#fafafa'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      {/* Left Side - Document Info */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          flex: 1,
                          minWidth: 0
                        }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.05)'
                                : '#f3f4f6',
                            color: '#6b7280',
                            flexShrink: 0
                          }}
                        >
                          {getFileTypeIcon(item.type, 14)}
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Title */}
                          <Typography
                            sx={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: theme.palette.text.primary,
                              textAlign: 'left',
                              mb: 0.5,
                              lineHeight: 1.4,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.name}
                          </Typography>

                          {/* Metadata Row */}
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap'
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary,
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexWrap: 'wrap'
                              }}
                            >
                              {[
                                item.size !== undefined
                                  ? formatFileSize(item.size)
                                  : null,
                                formatDateDisplay(item.createdAt, 'MMM d, yyyy')
                              ]
                                .filter(Boolean)
                                .join(' • ')}
                            </Typography>

                            {/* Session Tag */}
                            {item.sessionId && item.sessionTitle && (
                              <Chip
                                label={item.sessionTitle}
                                size="small"
                                icon={<MicrophoneIcon size={12} />}
                                sx={{
                                  height: 20,
                                  fontSize: '11px',
                                  cursor: 'pointer'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sessionNavigationService.navigateToSession(
                                    item.sessionId!,
                                    navigate
                                  );
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>

                      {/* Right Side - Actions Menu */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          flexShrink: 0
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePreviewFile(item);
                          }}
                          aria-label={`Preview ${item.name}`}
                          sx={{
                            borderRadius: '6px',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.text.primary,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.08)'
                                  : '#f3f4f6'
                            }
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleItemMenuClick(e, item)}
                          sx={{
                            borderRadius: '6px',
                            color: theme.palette.text.secondary,
                            '&:hover': {
                              color: theme.palette.text.primary,
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.08)'
                                  : '#f3f4f6'
                            }
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                ))}
              </Stack>
            )}
                </Box>
              </TabPanel>
            </Box>
          </Box>
        </Grid>

        {/* Right Column - Ask Panel */}
        {collection?.id && !isMobile && (
          <Grid
            size={{ xs: 12, md: 5.4 }}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
              pr: 0
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                bgcolor: 'background.paper',
                borderRadius: '10px',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }}
            >
              {/* Ask Tab Header */}
              <Box
                sx={{
                  display: 'flex',
                  px: 4,
                  pt: 0,
                  pb: 0,
                  height: '48px',
                  alignItems: 'center',
                  background: theme.palette.background.default,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    borderRadius: '6px',
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.2)'
                      : theme.palette.divider,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.02)',
                    color: theme.palette.primary.main,
                    fontSize: '13px',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    position: 'relative',
                    mb: '-1px',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: '-9px',
                      left: 0,
                      right: 0,
                      height: '2px',
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '2px 2px 0 0'
                    }
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      opacity: 0.7
                    }}
                  >
                    <Lightning size={16} weight="regular" />
                  </Box>
                  Ask
                </Box>
              </Box>

              {/* Chat Interface */}
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <CollectionMultiChatUI collectionId={collection.id} compact={true} />
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>

      {/* Edit Collection Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Edit Collection</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Reference Number"
              value={editedReferenceNumber}
              onChange={(e) => setEditedReferenceNumber(e.target.value)}
              fullWidth
            />
            <TextField
              label="Description"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editedTitle.trim()}
            sx={{ textTransform: 'none' }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Recording Dialog */}
      <Dialog
        open={addRecordingDialogOpen}
        onClose={() => {
          setAddRecordingDialogOpen(false);
          setSessionSearchTerm('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            height: '70vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Add Recording to Collection</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search recordings..."
            value={sessionSearchTerm}
            onChange={(e) => setSessionSearchTerm(e.target.value)}
            size="small"
            sx={{
              mt: 1,
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
          />

          {loadingSessions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableSessions.length > 0 ? (
            <>
              <List sx={{ overflow: 'auto', maxHeight: 'calc(70vh - 280px)' }}>
                {availableSessions.map((session) => {
                  const isSelected = selectedSessionIds.has(session.id);
                  return (
                    <ListItem key={session.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        onClick={() => handleToggleSessionSelection(session.id)}
                        sx={{
                          borderRadius: '8px',
                          border: `1px solid ${
                            isSelected
                              ? theme.palette.primary.main
                              : theme.palette.divider
                          }`,
                          backgroundColor: isSelected
                            ? theme.palette.mode === 'dark'
                              ? 'rgba(139, 92, 246, 0.1)'
                              : 'rgba(139, 92, 246, 0.05)'
                            : 'transparent',
                          '&:hover': {
                            backgroundColor: isSelected
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(139, 92, 246, 0.15)'
                                : 'rgba(139, 92, 246, 0.08)'
                              : theme.palette.mode === 'dark'
                              ? 'rgba(255, 255, 255, 0.05)'
                              : '#fafafa'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {isSelected ? (
                            <CheckBoxIcon
                              sx={{ color: theme.palette.primary.main }}
                            />
                          ) : (
                            <CheckBoxOutlineBlankIcon
                              sx={{ color: theme.palette.text.secondary }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor:
                                theme.palette.mode === 'dark'
                                  ? 'rgba(75, 94, 219, 0.2)'
                                  : '#F0F4FF',
                              color: '#4B5EDB'
                            }}
                          >
                            <MicIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={session.title || 'Untitled Session'}
                          secondary={formatDateDisplay(
                            session.sessionStartTime,
                            'MMM d, yyyy • h:mm a'
                          )}
                          primaryTypographyProps={{
                            fontWeight: 600,
                            fontSize: '14px'
                          }}
                          secondaryTypographyProps={{
                            fontSize: '12px'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>

              {/* Pagination */}
              {sessionTotalPages > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    pb: 2
                  }}
                >
                  <Pagination
                    count={sessionTotalPages}
                    page={sessionPage}
                    onChange={handleSessionPageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 1,
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">
                {sessionSearchTerm
                  ? 'No recordings match your search'
                  : 'No available recordings'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedSessionIds.size > 0 &&
              `${selectedSessionIds.size} selected`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                setAddRecordingDialogOpen(false);
                setSessionSearchTerm('');
                setSelectedSessionIds(new Set());
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedSessions}
              variant="contained"
              disabled={selectedSessionIds.size === 0 || isAddingSessions}
              sx={{ textTransform: 'none' }}
            >
              {isAddingSessions ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Adding...
                </>
              ) : (
                `Add ${
                  selectedSessionIds.size > 0
                    ? `(${selectedSessionIds.size})`
                    : ''
                }`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Add Document Dialog */}
      <Dialog
        open={addDocumentDialogOpen}
        onClose={() => {
          if (isUploadingDocument) return;
          setAddDocumentDialogOpen(false);
          setSelectedFile(null);
          setDocumentName('');
          setDocumentUploadError(null);
          if (documentFileInputRef.current) {
            documentFileInputRef.current.value = '';
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>Upload Document</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              sx={{
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: '8px',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: isUploadingDocument ? 0.6 : 1,
                pointerEvents: isUploadingDocument ? 'none' : 'auto',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? 'rgba(139, 92, 246, 0.05)'
                      : '#F5F3FF'
                }
              }}
              onClick={() => documentFileInputRef.current?.click()}
            >
              <input
                id="collection-document-upload"
                type="file"
                hidden
                ref={documentFileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.txt,.rtf,.md,.json,.csv,.png,.jpg,.jpeg,.gif,.bmp"
              />
              <PlusCircle
                size={48}
                style={{ color: theme.palette.text.disabled, marginBottom: '8px' }}
              />
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                {selectedFile ? selectedFile.name : 'Click to select a file'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedFile
                  ? formatFileSize(selectedFile.size)
                  : 'PDF, DOC, DOCX, TXT, or images'}
              </Typography>
            </Box>

            {documentUploadError && (
              <Alert severity="error">
                <AlertTitle>Error</AlertTitle>
                {documentUploadError}
              </Alert>
            )}

            <TextField
              label="Document Name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              fullWidth
              required
              placeholder="Enter a name for this document"
              disabled={isUploadingDocument}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              if (isUploadingDocument) return;
              setAddDocumentDialogOpen(false);
              setSelectedFile(null);
              setDocumentName('');
              setDocumentUploadError(null);
              if (documentFileInputRef.current) {
                documentFileInputRef.current.value = '';
              }
            }}
            sx={{ textTransform: 'none' }}
            disabled={isUploadingDocument}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocument}
            variant="contained"
            disabled={
              !selectedFile || !documentName.trim() || isUploadingDocument
            }
            sx={{ textTransform: 'none' }}
          >
            {isUploadingDocument ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <DocumentPreviewDialog
        open={Boolean(previewItem)}
        title={previewItem?.name || 'File preview'}
        previewUrl={previewUrl}
        contentType={previewContentType}
        fileFormat={previewItem?.fileFormat}
        loading={previewLoading}
        error={previewError}
        onClose={handlePreviewClose}
        onDownload={
          previewItem ? () => handleDownloadFile(previewItem) : undefined
        }
      />

      {/* File Actions Menu (Reused/Consolidated) */}
      <Menu
        anchorEl={fileMenuAnchor}
        open={Boolean(fileMenuAnchor)}
        onClose={handleFileMenuClose}
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        {selectedItem && selectedItem.type !== FileType.Folder && (
          <>
            <MenuItem
              onClick={() => {
                if (selectedItem) {
                  handlePreviewFile(selectedItem);
                }
                handleFileMenuClose();
              }}
            >
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Preview</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedItem) {
                  handleDownloadFile(selectedItem);
                }
                handleFileMenuClose();
              }}
            >
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Download</ListItemText>
            </MenuItem>
            {appUser?.isSuperAdmin && (
              <MenuItem
                onClick={() => {
                  if (selectedItem) {
                    handleRegenerateFile(selectedItem);
                  }
                }}
                disabled={
                  !!selectedItem && regeneratingDocumentIds.has(selectedItem.id)
                }
              >
                <ListItemIcon>
                  {selectedItem && regeneratingDocumentIds.has(selectedItem.id) ? (
                    <CircularProgress size={18} />
                  ) : (
                    <RegenerateIcon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText>Regenerate</ListItemText>
              </MenuItem>
            )}
            <MenuItem onClick={handleFileMenuClose}>
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleFileMenuClose}>
              <ListItemIcon>
                <StarBorderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Star</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleFileMenuClose}>
              <ListItemIcon>
                <EditOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Rename</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem
          onClick={() => {
            if (selectedItem) {
              handleDeleteClick(selectedItem);
            }
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon>
            <DeleteIcon
              fontSize="small"
              sx={{ color: theme.palette.error.main }}
            />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            Delete
          </ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        id="folder-session-actions-menu"
        anchorEl={sessionMenuAnchor}
        open={Boolean(sessionMenuAnchor)}
        onClose={handleSessionMenuClose}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <MenuItem onClick={() => handleOpenSessionActionDialog('remove')}>
          <ListItemIcon>
            <RemoveCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Remove from folder</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleOpenSessionActionDialog('delete')}
          sx={{ color: theme.palette.error.main }}
        >
          <ListItemIcon>
            <DeleteIcon
              fontSize="small"
              sx={{ color: theme.palette.error.main }}
            />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            Delete session
          </ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={Boolean(sessionActionDialog)}
        onClose={handleSessionActionCancel}
        onConfirm={handleSessionActionConfirm}
        title={
          sessionActionDialog === 'delete'
            ? 'Delete Session'
            : 'Remove from Folder'
        }
        message={
          sessionActionDialog === 'delete' ? (
            <>
              Delete{' '}
              <strong>
                "{selectedSessionForAction?.sessionTitle || 'Untitled Session'}"
              </strong>
              ? This will delete the session, all linked documents, and their
              blobs from storage.
            </>
          ) : (
            <>
              Remove{' '}
              <strong>
                "{selectedSessionForAction?.sessionTitle || 'Untitled Session'}"
              </strong>{' '}
              from this folder? The session and its documents will remain
              available outside this folder.
            </>
          )
        }
        confirmText={sessionActionDialog === 'delete' ? 'Delete' : 'Remove'}
        loadingText={
          sessionActionDialog === 'delete' ? 'Deleting...' : 'Removing...'
        }
        variant={sessionActionDialog === 'delete' ? 'danger' : 'warning'}
        isLoading={isSessionActionLoading}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Remove from Folder"
        message={<>Are you sure you want to remove <strong>"{itemToDelete?.name}"</strong> from this folder?</>}
        confirmText="Remove"
        variant="danger"
        isLoading={isDeleting}
      />
    </Box>
  );
};
